// Owner: ai-client (wave 1), copilot-panel (wave 2). `useAiAvailability()` (plan §4.5, `ai.md` F-19): AI
// entry points exist only where AI works. Members see nothing while AI is off or not connected; admins
// see one "Connect AI" link; with the monthly budget used up, entry points stay visible but disabled and
// say why. Light module: it loads with every editor top bar.
import { useQuery } from '@tanstack/react-query';
import type { AiCapability, AiStatusDTO } from '@dataroom/contracts/ai/status';
import { getAiStatus } from '@dataroom/contracts/ai/status';
import { api } from '../lib/api';
import { meQuery } from '../lib/queries';

export type AiAvailabilityState =
  /** Status not known yet: render nothing (no flicker of entry points). */
  | 'loading'
  /** AI works for this member: show entry points. */
  | 'ready'
  /** AI is off or not connected and the member cannot change that: show nothing. */
  | 'hidden'
  /** AI is off or not connected and the member is an admin: show one "Connect AI" link. */
  | 'connect'
  /** Connected, but the budget is used up: entry points show disabled with the reason. */
  | 'limited';

export type AiAvailability = {
  state: AiAvailabilityState;
  ready: boolean;
  status: AiStatusDTO | null;
  /** When `limited`: when the budget resets (ISO). */
  resetsAt: string | null;
};

export const AI_STATUS_QUERY_KEY = ['ai', 'status'] as const;

const PUBLIC_PATH = /^\/(f|p)\//;

/** Pure: the availability of a status for a capability. */
export function availabilityOf(
  status: AiStatusDTO | null | undefined,
  capability: AiCapability = 'agent',
  failed = false,
): AiAvailability {
  if (!status) return { state: failed ? 'hidden' : 'loading', ready: false, status: null, resetsAt: null };
  if (status.reason === 'budget_exhausted') {
    const hard = status.budgets.find((b) => b.hardReached);
    return { state: 'limited', ready: false, status, resetsAt: hard?.resetsAt ?? null };
  }
  const usable = status.available && status.capabilities[capability] !== false;
  if (usable) return { state: 'ready', ready: true, status, resetsAt: null };
  return { state: status.canManage ? 'connect' : 'hidden', ready: false, status, resetsAt: null };
}

/** The member's AI availability (shared query; refreshed when the window regains focus). */
export function useAiAvailability(capability: AiCapability = 'agent'): AiAvailability {
  // Only a signed-in member has a status: the probe waits for `/me` (read from the cache, never
  // fetched here), so a signed-out visit does not log a 401 for it.
  const me = useQuery({ ...meQuery, enabled: false });
  const query = useQuery({
    queryKey: AI_STATUS_QUERY_KEY,
    queryFn: ({ signal }) => api.call(getAiStatus, undefined, { signal }),
    staleTime: 60_000,
    retry: false,
    // Public pages (forms /f/…, published pages /p/…) have no signed-in member to ask for.
    enabled:
      Boolean(me.data) && !PUBLIC_PATH.test(typeof window === 'undefined' ? '' : window.location.pathname),
  });
  return availabilityOf(query.data, capability, query.isError);
}
